/**
 * codex.md RBAC admin UI.
 *
 * Two tabs (Pages / Graphs) each rendering a matrix of role × key
 * checkboxes. Rows are roles, columns are keys. A click toggles
 * isVisible via PATCH /permissions/{page,graph}; on success the local
 * grid state updates and the row's role cache is invalidated on the
 * server so the affected user sees the change on their next permission
 * refresh (AuthProvider refetches on focus).
 *
 * Route-level gate lives in AppShell (pageKey='permissions' + legacy
 * requiresSuperAdmin flag); this page additionally hides the save
 * controls if the active user isn't SUPER_ADMIN, as a defence-in-depth.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../../../services/api";
import { useAuth } from "../../../../app/providers/AuthProvider";

// Roles shown in the permissions matrix. Legacy values (ADMIN / MANAGER /
// CONSULTANT / CLIENT_VIEW) stay in the UserRole enum for existing users
// but are no longer configurable here — the backend seed deletes any rows
// carrying those roles on startup. Assign affected users to a new role
// from the Team & Access page.
const ALL_ROLES = [
  "SUPER_ADMIN",
  "CEO",
  "CFO",
  "COO",
  "CCO",
  "PM",
  "HR",
];

const PAGE_KEYS = [
  "dashboard",
  "mytasks",
  "scoping",
  "projects",
  "resources",
  "clients",
  "financial",
  "reports",
  "leads",
  "audits",
  "mentions",
  "monday",
  "team",
  "permissions",
];

// Fine-grained graph keys. Keep in sync with ALL_GRAPH_KEYS in
// server/src/seeds/defaultPermissions.ts and the canSeeGraph calls on
// FinancialStatusPage.jsx. Ordered so related widgets sit together in
// the matrix header — top: KPI rows, then P&L, then supporting charts,
// then legacy broad keys.
const GRAPH_KEYS = [
  "fin_kpi_pl",
  "fin_kpi_ops",
  "fin_kpi_project_health",
  "fin_kpi_insights",
  "fin_chart_revenue",
  "fin_chart_cost",
  "fin_chart_profit",
  "fin_chart_monthly_billing",
  "fin_chart_waterfall_legacy",
  "fin_chart_utilisation_forecast",
  "fin_chart_utilisation_individuals",
  "fin_chart_monthly_billables",
  "fin_chart_profit_margin",
  "fin_chart_avg_hourly_rate",
  "fin_chart_nps",
  "fin_chart_headcount",
  "fin_chart_utilisation_heatmap",
  "fin_table_project_rate_util",
  "fin_chart_top_clients",
  "fin_chart_pipeline",
  // Legacy broad keys — matrix includes them so admins can retire any
  // still-lingering usage of the old coarse keys.
  "revenue_graph",
  "cost_graph",
  "profit_graph",
  "utilisation_graph",
  "headcount_graph",
  "kpi_cards",
];

const GRAPH_LABELS = {
  fin_kpi_pl:                       "KPI · P&L Row",
  fin_kpi_ops:                      "KPI · Ops Row",
  fin_kpi_project_health:           "KPI · Project Health",
  fin_kpi_insights:                 "KPI · Insight Strip",
  fin_chart_revenue:                "Chart · Revenue",
  fin_chart_cost:                   "Chart · Cost",
  fin_chart_profit:                 "Chart · Profit",
  fin_chart_monthly_billing:        "Chart · Monthly Billing",
  fin_chart_waterfall_legacy:       "Chart · Waterfall (legacy)",
  fin_chart_utilisation_forecast:   "Chart · Utilisation Forecast",
  fin_chart_utilisation_individuals:"Chart · Per-Consultant Utilisation",
  fin_chart_monthly_billables:      "Chart · Monthly Billables",
  fin_chart_profit_margin:          "Chart · Profit Margin %",
  fin_chart_avg_hourly_rate:        "Chart · Avg Hourly Rate",
  fin_chart_nps:                    "Chart · NPS Client Satisfaction",
  fin_chart_headcount:              "Chart · Headcount Stacked",
  fin_chart_utilisation_heatmap:    "Chart · Utilisation Heatmap",
  fin_table_project_rate_util:      "Table · Per-Project Rate & Util",
  fin_chart_top_clients:            "Chart · Top Clients",
  fin_chart_pipeline:               "Chart · Pipeline by Stage",
  revenue_graph:                    "(legacy) revenue_graph",
  cost_graph:                       "(legacy) cost_graph",
  profit_graph:                     "(legacy) profit_graph",
  utilisation_graph:                "(legacy) utilisation_graph",
  headcount_graph:                  "(legacy) headcount_graph",
  kpi_cards:                        "(legacy) kpi_cards",
};

const ROLE_LABEL = {
  SUPER_ADMIN: "Super Admin",
  CEO: "CEO",
  CFO: "CFO",
  COO: "COO",
  CCO: "CCO",
  PM: "PM",
  HR: "HR",
};

function PermissionMatrix({ title, subtitle, keys, keyField, keyLabels, lookup, disabled, pendingKey, onToggle }) {
  const labelFor = (k) => (keyLabels && keyLabels[k]) || k.replace(/_/g, " ");
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--rc-text)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "var(--rc-text-secondary)", marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--rc-border)", borderRadius: 8, background: "var(--rc-surface-raised, #0a1528)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,.04)" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "var(--rc-text-secondary)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", borderBottom: "1px solid var(--rc-border)", position: "sticky", left: 0, background: "#0a1528", zIndex: 1 }}>
                Role
              </th>
              {keys.map((k) => (
                <th key={k} title={k} style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: "var(--rc-text)", borderBottom: "1px solid var(--rc-border)", minWidth: 110, fontSize: 11, whiteSpace: "nowrap" }}>
                  {labelFor(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_ROLES.map((role) => (
              <tr key={role} style={{ borderBottom: "1px solid var(--rc-border)" }}>
                <td style={{ padding: "8px 12px", fontWeight: 600, color: "var(--rc-text)", position: "sticky", left: 0, background: "#0a1528", zIndex: 1, borderRight: "1px solid var(--rc-border)" }}>
                  {ROLE_LABEL[role] || role}
                </td>
                {keys.map((k) => {
                  const isOn = lookup.get(`${role}|${k}`) === true;
                  const cellDisabled = disabled || pendingKey === `${keyField}|${role}|${k}`;
                  return (
                    <td key={k} style={{ padding: "6px 6px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={isOn}
                        disabled={cellDisabled}
                        onChange={(e) => onToggle(role, k, e.target.checked, keyField)}
                        aria-label={`Toggle ${role} ${k}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PermissionsPage({ showToast }) {
  const { isSuperAdmin, realRole, activeRole, impersonating, setImpersonateRole } = useAuth();
  const [grid, setGrid] = useState({ pages: [], graphs: [] });
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState(null);

  // AuthProvider polls /permissions/me every 10s, which re-renders every
  // component that calls useAuth(). If showToast isn't memoised upstream
  // (AppShell passes a fresh arrow every render), binding loadGrid to
  // showToast via useCallback would cause loadGrid's useEffect to fire
  // every 10s — re-fetching the grid and making the page flicker.
  // Route showToast through a ref so loadGrid has stable identity.
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.getPermissionGrid();
      setGrid({
        pages: Array.isArray(result?.pages) ? result.pages : [],
        graphs: Array.isArray(result?.graphs) ? result.graphs : [],
      });
    } catch (err) {
      showToastRef.current?.("Error", err?.response?.data?.error || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fire once on mount; admin explicitly hits refresh (or re-navigates)
  // to pull again. The permissions poll on AuthProvider updates the
  // caller's own view, not the grid — those are different concerns.
  useEffect(() => { loadGrid(); }, [loadGrid]);

  const pageLookup = useMemo(() => {
    const map = new Map();
    for (const row of grid.pages) map.set(`${row.role}|${row.pageKey}`, row.isVisible);
    return map;
  }, [grid.pages]);
  const graphLookup = useMemo(() => {
    const map = new Map();
    for (const row of grid.graphs) map.set(`${row.role}|${row.graphKey}`, row.isVisible);
    return map;
  }, [grid.graphs]);

  async function onToggle(role, key, nextValue, kind) {
    const cacheKey = `${kind}|${role}|${key}`;
    if (pendingKey === cacheKey) return;
    setPendingKey(cacheKey);
    try {
      if (kind === "pages") {
        await apiClient.upsertPagePermission({ role, pageKey: key, isVisible: nextValue });
      } else {
        await apiClient.upsertGraphPermission({ role, graphKey: key, isVisible: nextValue });
      }
      await loadGrid();
      if (impersonating && activeRole === role) {
        await setImpersonateRole(role);
      }
      showToastRef.current?.("Saved", `${role} · ${key} · ${nextValue ? "visible" : "hidden"}`);
    } catch (err) {
      showToastRef.current?.("Error", err?.response?.data?.error || "Failed to toggle permission");
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <div className="fade" style={{ padding: "32px 36px" }}>
      <div>
        <div className="pg-title">Permissions</div>
        <div className="pg-sub">
          Which roles can see which pages AND which graphs. Cached 30s server-side; users pick up changes on
          tab-focus or next page load. SUPER_ADMIN + CEO always see everything regardless of the matrix below.
        </div>
      </div>

      {/* SUPER_ADMIN-only preview switcher. Real role stays SUPER_ADMIN on
          the backend; only this client pretends to be the chosen role so
          canSeePage / canSeeGraph reflect that role's grid. Lets you test
          a toggle immediately without logging out. */}
      {isSuperAdmin && (
        <div style={{ marginTop: 16, padding: "12px 14px", border: "1px solid var(--rc-border)", borderRadius: 8,
          background: impersonating ? "rgba(245,158,11,.10)" : "rgba(96,165,250,.06)",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "var(--rc-text-secondary)" }}>
            <strong style={{ color: "var(--rc-text)" }}>View as role:</strong>{" "}
            {impersonating
              ? <>Previewing as <strong style={{color:"#f59e0b"}}>{activeRole}</strong>. Sidebar + pages reflect this role now.</>
              : <>You are <strong>{realRole}</strong>. Toggles below take effect for other roles — pick one here to preview.</>}
          </div>
          <select
            value={activeRole || ""}
            onChange={(e) => setImpersonateRole(e.target.value || null)}
            style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid var(--rc-border)", background: "#070e20", color: "var(--rc-text)", fontSize: 12 }}
          >
            {ALL_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r] || r}{r === realRole ? " (you)" : ""}</option>
            ))}
          </select>
          {impersonating && (
            <button
              onClick={() => setImpersonateRole(null)}
              style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--rc-border)", background: "#070e20", color: "#8fa4c8", cursor: "pointer", fontSize: 12 }}
            >
              ✕ Stop previewing
            </button>
          )}
        </div>
      )}

      {loading && <div style={{ color: "var(--rc-text-secondary)", marginTop: 16 }}>Loading permissions…</div>}

      {!loading && (
        <>
          <PermissionMatrix
            title="Pages"
            subtitle="Which sidebar items and routes each role can access. Backend gates routes; frontend hides nav items."
            keys={PAGE_KEYS}
            keyField="pages"
            lookup={pageLookup}
            disabled={!isSuperAdmin}
            pendingKey={pendingKey}
            onToggle={onToggle}
          />
          <PermissionMatrix
            title="Graphs"
            subtitle="Per-widget visibility on the Financial page. fin_kpi_* toggles whole KPI rows; fin_chart_* targets an individual chart by its header title. Legacy keys (revenue_graph, cost_graph, etc) are kept until the last call-sites are migrated."
            keys={GRAPH_KEYS}
            keyField="graphs"
            keyLabels={GRAPH_LABELS}
            lookup={graphLookup}
            disabled={!isSuperAdmin}
            pendingKey={pendingKey}
            onToggle={onToggle}
          />
        </>
      )}
    </div>
  );
}
